import {
    collection,
    doc,
    getFirestore,
    onSnapshot,
    runTransaction,
    serverTimestamp,
    increment,
} from './instrumentedFirestore';
import { getFirebaseApp } from './app';
import { COLLECTIONS, financeSummaryDocPath } from '@constants/firestorePaths';

function db() {
    return getFirestore(getFirebaseApp());
}

function stripUndefinedDeep(value) {
    if (value === undefined) return undefined;
    if (value === null) return null;
    // Preserve special Firestore / Date values as-is
    if (value instanceof Date) return value;
    if (typeof value?.toDate === 'function') return value;
    // Preserve Firestore FieldValue-like objects (serverTimestamp(), increment(), etc.)
    if (typeof value?._methodName === 'string') return value;
    if (Array.isArray(value)) {
        return value.map(stripUndefinedDeep).filter((v) => v !== undefined);
    }
    if (typeof value === 'object') {
        // Only recurse into plain objects; keep class instances (e.g. FieldValue) intact.
        const proto = Object.getPrototypeOf(value);
        const isPlain = proto === Object.prototype || proto === null;
        if (!isPlain) return value;
        const next = {};
        for (const [k, v] of Object.entries(value)) {
            if (v === undefined) continue;
            const cleaned = stripUndefinedDeep(v);
            if (cleaned !== undefined) next[k] = cleaned;
        }
        return next;
    }
    return value;
}

export const FINANCE_ACCOUNT_TYPES = {
    COH: 'COH',
    BANK: 'BANK',
};

export const FINANCE_ENTRY_TYPES = {
    RECEIVE: 'receive',
    EXPENSE: 'expense',
};

export function subscribeFinanceAccounts({ tenantId, onNext, onError }) {
    if (!tenantId) throw new Error('Missing tenantId');
    const ref = collection(db(), COLLECTIONS.TENANTS, tenantId, COLLECTIONS.FINANCE_ACCOUNTS);
    return onSnapshot(
        ref,
        (snap) => {
            const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            onNext?.(rows);
        },
        (err) => onError?.(err)
    );
}

export function subscribeFinanceSummary({ tenantId, onNext, onError }) {
    if (!tenantId) throw new Error('Missing tenantId');
    const ref = doc(db(), financeSummaryDocPath(tenantId));
    return onSnapshot(
        ref,
        (snap) => {
            const data = snap.exists() ? snap.data() : {};
            onNext?.(data);
        },
        (err) => onError?.(err)
    );
}

export function subscribeFinanceEntries({ tenantId, onNext, onError, type, accountId }) {
    if (!tenantId) throw new Error('Missing tenantId');
    const ref = collection(db(), COLLECTIONS.TENANTS, tenantId, COLLECTIONS.FINANCE_ENTRIES);
    return onSnapshot(
        ref,
        (snap) => {
            let rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            if (type) rows = rows.filter((r) => r.type === type);
            if (accountId) rows = rows.filter((r) => r.accountId === accountId);
            onNext?.(rows);
        },
        (err) => onError?.(err)
    );
}

export async function createFinanceAccount({ tenantId, name, type, openingBalance = 0 }) {
    if (!tenantId) throw new Error('Missing tenantId');
    if (!name) throw new Error('Missing account name');
    if (!type) throw new Error('Missing account type');

    const ref = doc(collection(db(), COLLECTIONS.TENANTS, tenantId, COLLECTIONS.FINANCE_ACCOUNTS));
    const now = serverTimestamp();
    const payload = {
        tenantId,
        name: String(name).trim(),
        type,
        openingBalance: Number(openingBalance || 0) || 0,
        balance: Number(openingBalance || 0) || 0,
        totalReceive: 0,
        totalExpense: 0,
        createdAt: now,
        updatedAt: now,
    };

    await runTransaction(db(), async (tx) => {
        tx.set(ref, payload, { merge: true });
    });

    return { accountId: ref.id };
}

/**
 * Post a finance entry and update:
 * - account totals (totalReceive, totalExpense, balance)
 * - summary totals (totalReceive, totalExpense, balance)
 *
 * This avoids scanning financeEntries to compute totals.
 */
export async function postFinanceEntry({
    tenantId,
    accountId,
    type,
    date,
    particulars,
    amount,
}) {
    if (!tenantId) throw new Error('Missing tenantId');
    if (!accountId) throw new Error('Missing accountId');
    if (type !== FINANCE_ENTRY_TYPES.RECEIVE && type !== FINANCE_ENTRY_TYPES.EXPENSE) {
        throw new Error('Invalid entry type');
    }
    const amtRaw = typeof amount === 'number' ? amount : Number(String(amount ?? '').replace(/[^\d]/g, ''));
    const amt = Number.isFinite(amtRaw) ? amtRaw : 0;
    if (!Number.isFinite(amt) || amt <= 0) throw new Error('Amount must be greater than 0');

    const entryRef = doc(collection(db(), COLLECTIONS.TENANTS, tenantId, COLLECTIONS.FINANCE_ENTRIES));
    const accountRef = doc(db(), COLLECTIONS.TENANTS, tenantId, COLLECTIONS.FINANCE_ACCOUNTS, accountId);
    const summaryRef = doc(db(), financeSummaryDocPath(tenantId));

    const isReceive = type === FINANCE_ENTRY_TYPES.RECEIVE;
    const signed = isReceive ? amt : -amt;

    const now = serverTimestamp();
    const localNow = new Date();
    const normalizedDate =
        date instanceof Date
            ? date
            : typeof date?.toDate === 'function'
                ? date.toDate()
                : new Date(date);
    const safeDate =
        normalizedDate instanceof Date && !Number.isNaN(normalizedDate.getTime())
            ? normalizedDate
            : localNow;

    const entryPayload = {
        tenantId,
        accountId,
        type,
        date: safeDate,
        particulars: String(particulars || '').trim(),
        amount: amt,
        createdAt: now,
        createdAtLocal: localNow,
        updatedAt: now,
        updatedAtLocal: localNow,
    };

    await runTransaction(db(), async (tx) => {
        // Ensure account exists
        const accSnap = await tx.get(accountRef);
        if (!accSnap.exists()) throw new Error('Account not found');

        tx.set(entryRef, entryPayload, { merge: true });

        tx.set(
            accountRef,
            {
                updatedAt: now,
                ...(isReceive ? { totalReceive: increment(amt) } : { totalExpense: increment(amt) }),
                balance: increment(signed),
            },
            { merge: true }
        );

        // Summary doc is tenant-wide analytics for fast KPIs
        tx.set(
            summaryRef,
            {
                tenantId,
                updatedAt: now,
                ...(isReceive ? { totalReceive: increment(amt) } : { totalExpense: increment(amt) }),
                balance: increment(signed),
            },
            { merge: true }
        );
    });

    return { entryId: entryRef.id };
}

export async function updateFinanceEntry({ tenantId, entryId, patch }) {
    if (!tenantId) throw new Error('Missing tenantId');
    if (!entryId) throw new Error('Missing entryId');
    if (!patch || typeof patch !== 'object') throw new Error('Missing patch');

    const entryRef = doc(db(), COLLECTIONS.TENANTS, tenantId, COLLECTIONS.FINANCE_ENTRIES, entryId);
    const summaryRef = doc(db(), financeSummaryDocPath(tenantId));

    const now = serverTimestamp();
    const localNow = new Date();

    const normalizeDate = (value) => {
        if (!value) return null;
        if (value instanceof Date) return value;
        if (typeof value?.toDate === 'function') return value.toDate();
        const d = new Date(value);
        return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
    };

    await runTransaction(db(), async (tx) => {
        const entrySnap = await tx.get(entryRef);
        if (!entrySnap.exists()) throw new Error('Entry not found');

        const prev = entrySnap.data() || {};
        const prevAccountId = prev.accountId;
        const prevType = prev.type;
        const prevAmt = Number(prev.amount || 0) || 0;

        if (!prevAccountId) throw new Error('Entry missing accountId');
        if (prevType !== FINANCE_ENTRY_TYPES.RECEIVE && prevType !== FINANCE_ENTRY_TYPES.EXPENSE) {
            throw new Error('Entry has invalid type');
        }

        const nextAccountId = patch.accountId || prevAccountId;
        const nextAmtRaw = patch.amount !== undefined ? patch.amount : prevAmt;
        const nextAmtParsed =
            typeof nextAmtRaw === 'number'
                ? nextAmtRaw
                : Number(String(nextAmtRaw ?? '').replace(/[^\d]/g, ''));
        const nextAmt = Number.isFinite(nextAmtParsed) ? nextAmtParsed : 0;
        if (!Number.isFinite(nextAmt) || nextAmt <= 0) throw new Error('Amount must be greater than 0');

        const prevSigned = prevType === FINANCE_ENTRY_TYPES.RECEIVE ? prevAmt : -prevAmt;
        const nextSigned = prevType === FINANCE_ENTRY_TYPES.RECEIVE ? nextAmt : -nextAmt;

        const deltaAmt = nextAmt - prevAmt;
        const deltaSigned = nextSigned - prevSigned;

        const prevAccountRef = doc(db(), COLLECTIONS.TENANTS, tenantId, COLLECTIONS.FINANCE_ACCOUNTS, prevAccountId);
        const nextAccountRef = doc(db(), COLLECTIONS.TENANTS, tenantId, COLLECTIONS.FINANCE_ACCOUNTS, nextAccountId);

        const prevAccSnap = await tx.get(prevAccountRef);
        if (!prevAccSnap.exists()) throw new Error('Account not found');
        if (nextAccountId !== prevAccountId) {
            const nextAccSnap = await tx.get(nextAccountRef);
            if (!nextAccSnap.exists()) throw new Error('Target account not found');
        }

        // Update entry doc
        const nextDate = patch.date !== undefined ? normalizeDate(patch.date) : undefined;
        const entryPatch = {
            ...(patch.particulars !== undefined ? { particulars: String(patch.particulars || '').trim() } : {}),
            ...(patch.accountId !== undefined ? { accountId: nextAccountId } : {}),
            ...(patch.amount !== undefined ? { amount: nextAmt } : {}),
            ...(patch.date !== undefined ? { date: nextDate || localNow } : {}),
            updatedAt: now,
            updatedAtLocal: localNow,
        };
        tx.set(entryRef, entryPatch, { merge: true });

        const isReceive = prevType === FINANCE_ENTRY_TYPES.RECEIVE;

        if (nextAccountId === prevAccountId) {
            // Same account: apply delta
            tx.set(
                prevAccountRef,
                {
                    updatedAt: now,
                    ...(isReceive ? { totalReceive: increment(deltaAmt) } : { totalExpense: increment(deltaAmt) }),
                    balance: increment(deltaSigned),
                },
                { merge: true }
            );
        } else {
            // Different account: rollback prev, apply next
            tx.set(
                prevAccountRef,
                {
                    updatedAt: now,
                    ...(isReceive ? { totalReceive: increment(-prevAmt) } : { totalExpense: increment(-prevAmt) }),
                    balance: increment(-prevSigned),
                },
                { merge: true }
            );
            tx.set(
                nextAccountRef,
                {
                    updatedAt: now,
                    ...(isReceive ? { totalReceive: increment(nextAmt) } : { totalExpense: increment(nextAmt) }),
                    balance: increment(nextSigned),
                },
                { merge: true }
            );
        }

        // Summary totals (tenant-wide)
        tx.set(
            summaryRef,
            {
                tenantId,
                updatedAt: now,
                ...(isReceive ? { totalReceive: increment(deltaAmt) } : { totalExpense: increment(deltaAmt) }),
                balance: increment(deltaSigned),
            },
            { merge: true }
        );
    });

    return { entryId };
}

export async function deleteFinanceEntry({ tenantId, entryId }) {
    if (!tenantId) throw new Error('Missing tenantId');
    if (!entryId) throw new Error('Missing entryId');

    const entryRef = doc(db(), COLLECTIONS.TENANTS, tenantId, COLLECTIONS.FINANCE_ENTRIES, entryId);
    const summaryRef = doc(db(), financeSummaryDocPath(tenantId));
    const now = serverTimestamp();

    await runTransaction(db(), async (tx) => {
        const entrySnap = await tx.get(entryRef);
        if (!entrySnap.exists()) throw new Error('Entry not found');
        const entry = entrySnap.data() || {};
        const accountId = entry.accountId;
        const type = entry.type;
        const amt = Number(entry.amount || 0) || 0;

        if (!accountId) throw new Error('Entry missing accountId');
        if (type !== FINANCE_ENTRY_TYPES.RECEIVE && type !== FINANCE_ENTRY_TYPES.EXPENSE) {
            throw new Error('Entry has invalid type');
        }

        const signed = type === FINANCE_ENTRY_TYPES.RECEIVE ? amt : -amt;
        const isReceive = type === FINANCE_ENTRY_TYPES.RECEIVE;

        const accountRef = doc(db(), COLLECTIONS.TENANTS, tenantId, COLLECTIONS.FINANCE_ACCOUNTS, accountId);
        const accSnap = await tx.get(accountRef);
        if (!accSnap.exists()) throw new Error('Account not found');

        // Adjust account totals
        tx.set(
            accountRef,
            {
                updatedAt: now,
                ...(isReceive ? { totalReceive: increment(-amt) } : { totalExpense: increment(-amt) }),
                balance: increment(-signed),
            },
            { merge: true }
        );

        // Adjust summary totals
        tx.set(
            summaryRef,
            {
                tenantId,
                updatedAt: now,
                ...(isReceive ? { totalReceive: increment(-amt) } : { totalExpense: increment(-amt) }),
                balance: increment(-signed),
            },
            { merge: true }
        );

        // Delete entry
        tx.delete(entryRef);
    });

    return { entryId };
}

